/**
 * IdleTimeoutWrapper
 *
 * Wraps children with idle timeout detection.
 * Shows a warning toast 1 minute before auto-logout.
 * Integrates with the superadmin portal's cookie-based auth.
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useIdleTimeout from '../hooks/useIdleTimeout';
import api from '../services/api';

const IDLE_TIMEOUT = 10 * 60 * 1000; // 10 minutes

export default function IdleTimeoutWrapper({ children }) {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);

  const handleIdle = useCallback(async () => {
    setShowWarning(false);
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore logout errors
    }
    localStorage.removeItem('superadminUser');
    navigate('/login', { replace: true });
  }, [navigate]);

  const handleWarning = useCallback(() => {
    setShowWarning(true);
  }, []);

  const { resetTimers } = useIdleTimeout({
    onIdle: handleIdle,
    onWarning: handleWarning,
    timeout: IDLE_TIMEOUT,
    enabled: true,
  });

  const dismissWarning = () => {
    setShowWarning(false);
    resetTimers();
  };

  return (
    <>
      {children}

      {/* Idle Warning Toast */}
      {showWarning && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm">
          <div className="bg-amber-500 text-white rounded-lg shadow-lg p-4 flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm">Session Expiring</p>
              <p className="text-xs mt-1 opacity-90">
                You will be logged out in 1 minute due to inactivity.
              </p>
            </div>
            <button
              onClick={dismissWarning}
              className="px-3 py-1 bg-white text-amber-600 text-xs font-semibold rounded hover:bg-amber-50 transition"
            >
              Stay
            </button>
          </div>
        </div>
      )}
    </>
  );
}
